/**
 * Computer Control Admin View
 *
 * Display status, configuration, and screenshots for OS-level computer control.
 */

import { html, nothing } from "lit";
import type { ComputerConfig, ComputerStatus } from "../controllers/computer.ts";

// --------------- Types ---------------

export type ComputerAdminProps = {
  status: ComputerStatus | null;
  config: ComputerConfig | null;
  loading: boolean;
  error: string | null;
  screenshot: string | null;
  screenshotLoading: boolean;
  onRefresh: () => void;
  onRefreshConfig: () => void;
  onScreenshot: () => void;
};

// --------------- Render ---------------

export function renderComputerAdmin(props: ComputerAdminProps) {
  return html`
    <div style="display:flex;flex-direction:column;gap:20px;">
      <!-- Header -->
      <div style="display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:12px;">
        <div></div>
        <div style="display:flex;gap:8px;">
          <button class="btn" @click=${props.onRefresh} ?disabled=${props.loading}>
            ${props.loading ? "Loading..." : "Refresh"}
          </button>
          <button
            class="btn primary"
            @click=${props.onScreenshot}
            ?disabled=${props.screenshotLoading || !props.status?.enabled}
          >
            ${props.screenshotLoading ? "Capturing..." : "Take Screenshot"}
          </button>
        </div>
      </div>

      ${props.error ? html`<div class="callout danger">${props.error}</div>` : nothing}

      <!-- Status Card -->
      ${props.status ? renderStatus(props.status) : nothing}
      ${
        !props.status && !props.loading
          ? html`
              <div class="callout info">
                Computer control status not available. The feature may be disabled or the igloobot-core plugin may
                not be loaded.
              </div>
            `
          : nothing
      }

      <!-- Config Card -->
      ${props.config ? renderConfig(props.config) : nothing}

      <!-- Screenshot -->
      ${props.screenshot ? renderScreenshot(props.screenshot) : nothing}
    </div>
  `;
}

// --------------- Sub-renders ---------------

function renderStatus(status: ComputerStatus) {
  return html`
    <div class="card">
      <div class="card-title">Service Status</div>
      <div class="card-sub">Current state of the computer control subsystem.</div>
      <div style="margin-top:16px;display:grid;grid-template-columns:repeat(auto-fill,minmax(200px,1fr));gap:16px;">
        <div>
          <div class="muted" style="font-size:0.85em;margin-bottom:4px;">Enabled</div>
          <span class="chip ${status.enabled ? "chip-ok" : "chip-warn"}">
            ${status.enabled ? "Yes" : "No"}
          </span>
        </div>
        <div>
          <div class="muted" style="font-size:0.85em;margin-bottom:4px;">Virtual Display</div>
          <span class="chip ${status.display.running ? "chip-ok" : "chip-warn"}">
            ${status.display.running ? "Running" : "Stopped"}
          </span>
          ${
            status.display.displayId
              ? html`<span class="mono" style="margin-left:8px;font-size:0.85em;">${status.display.displayId}</span>`
              : nothing
          }
          ${
            status.display.resolution
              ? html`<span class="muted" style="margin-left:8px;font-size:0.85em;">${status.display.resolution}</span>`
              : nothing
          }
        </div>
        <div>
          <div class="muted" style="font-size:0.85em;margin-bottom:4px;">VNC Server</div>
          <span class="chip ${status.vnc.running ? "chip-ok" : "chip-warn"}">
            ${status.vnc.running ? "Running" : "Stopped"}
          </span>
          ${
            status.vnc.port
              ? html`<span class="mono" style="margin-left:8px;font-size:0.85em;">:${status.vnc.port}</span>`
              : nothing
          }
        </div>
        <div>
          <div class="muted" style="font-size:0.85em;margin-bottom:4px;">Active Sessions</div>
          <strong>${status.activeSessions}</strong>
        </div>
      </div>
    </div>
  `;
}

function renderConfig(config: ComputerConfig) {
  return html`
    <div class="card">
      <div class="card-title">Configuration</div>
      <div class="card-sub">Current computer control configuration. Edit via the Config tab or config file.</div>
      <div style="margin-top:16px;">
        <div class="table">
          <div class="table-head">
            <span style="flex:1">Setting</span>
            <span style="flex:2">Value</span>
          </div>
          <div class="table-row">
            <span style="flex:1" class="muted">Enabled</span>
            <span style="flex:2">${config.enabled ? "Yes" : "No"}</span>
          </div>
          <div class="table-row">
            <span style="flex:1" class="muted">Display Resolution</span>
            <span style="flex:2" class="mono">${config.displayResolution}</span>
          </div>
          <div class="table-row">
            <span style="flex:1" class="muted">Color Depth</span>
            <span style="flex:2">${config.colorDepth}-bit</span>
          </div>
          <div class="table-row">
            <span style="flex:1" class="muted">VNC Port</span>
            <span style="flex:2" class="mono">${config.vncPort}</span>
          </div>
        </div>
      </div>
    </div>
  `;
}

function renderScreenshot(image: string) {
  return html`
    <div class="card">
      <div class="card-title">Screenshot</div>
      <div class="card-sub">Latest captured screenshot from the virtual display.</div>
      <div style="margin-top:12px;text-align:center;">
        <img
          src=${image.startsWith("data:") ? image : `data:image/png;base64,${image}`}
          alt="Screenshot"
          style="max-width:100%;border:1px solid var(--border);border-radius:8px;"
        />
      </div>
    </div>
  `;
}
